import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  MarketDecimals,
  getMarketDecimals,
  subscribeMarketDecimals,
} from "../utils/rawConversion";

/**
 * A market's decimals, re-rendering the caller when they become known.
 *
 * The decimals registry is a module object filled by an SWR fetcher side
 * effect (`useGetMarkets`), not React state. Anything that read it during
 * render was therefore frozen at whatever was there at the time, with no
 * re-render when `/markets` landed — an order book converted before that point
 * kept prices 10^12 out for the life of the page, and the wrong numbers
 * reached order sizing, not just display.
 *
 * `useSyncExternalStore` is the React 18 primitive for exactly this shape: an
 * external mutable store whose changes must schedule a render. Returns
 * `undefined` while the decimals are unknown — callers must render a pending
 * state rather than convert with a guess.
 *
 * The subscribed snapshot is a STRING, not the decimals object. React compares
 * snapshots by identity and re-renders until two agree, so a store that
 * returned a fresh object per call would spin forever ("Maximum update depth
 * exceeded"). A primitive cannot, whatever the store does.
 */
export const useMarketDecimals = (
  instrumentKey?: string
): MarketDecimals | undefined => {
  const getSnapshot = useCallback(() => {
    const decimals = instrumentKey ? getMarketDecimals(instrumentKey) : undefined;
    return decimals
      ? `${decimals.baseDecimals}:${decimals.quoteDecimals}`
      : "";
  }, [instrumentKey]);

  const snapshot = useSyncExternalStore(
    subscribeMarketDecimals,
    getSnapshot,
    getSnapshot
  );

  // One stable object per distinct pair, so consumers can safely use it as a
  // hook dependency without re-running on every render.
  return useMemo(() => {
    if (!snapshot) return undefined;
    const [baseDecimals, quoteDecimals] = snapshot.split(":").map(Number);
    return { baseDecimals, quoteDecimals };
  }, [snapshot]);
};

export default useMarketDecimals;
